import React, { useState, useMemo } from 'react';
import { PendingAction, RiskLevel, WorkstreamType } from '../types';
import { Search, Filter, CheckCircle2, XCircle, Eye, ShieldAlert, Clock, Sparkles, Download } from 'lucide-react';

export interface ActionQueueProps {
  actions: PendingAction[];
  totalPending?: number;
  loading: boolean;
  view?: 'pending' | 'approved';
  onDownloadPacket?: (actionId: string) => Promise<unknown>;
  onApprove: (actionId: string) => Promise<void>;
  onReject: (actionId: string, reason: string) => Promise<void>;
  onBatchApprove?: (actionIds: string[]) => Promise<void>;
  onBatchReject?: (actionIds: string[], reason: string) => Promise<void>;
  onSelectAction: (action: PendingAction) => void;
}

const workstreamBadges: Record<string, string> = {
  marketing: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  referrals: 'bg-mantis-50 text-mantis-700 border-mantis-200',
  intake: 'bg-blue-50 text-blue-700 border-blue-200',
  scheduling: 'bg-teal-50 text-teal-700 border-teal-200',
  rcm: 'bg-amber-50 text-amber-700 border-amber-200',
  hr: 'bg-purple-50 text-purple-700 border-purple-200',
  finance: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  'clinical-ops': 'bg-redwood-50 text-redwood-700 border-redwood-200',
};

const riskBadges: Record<string, string> = {
  low: 'bg-mantis-50 text-mantis-700 border-mantis-200',
  medium: 'bg-amber-50 text-amber-700 border-amber-200',
  high: 'bg-orange-50 text-orange-700 border-orange-200',
  critical: 'bg-redwood-50 text-redwood-700 border-redwood-200 font-semibold',
};

export const ActionQueue: React.FC<ActionQueueProps> = ({
  actions,
  totalPending,
  loading,
  view = 'pending',
  onDownloadPacket,
  onApprove,
  onReject,
  onSelectAction,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedWorkstream, setSelectedWorkstream] = useState<string>('all');
  const [selectedRisk, setSelectedRisk] = useState<string>('all');

  // Single action rejection modal state
  const [rejectingActionId, setRejectingActionId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processingId, setProcessingId] = useState<string | null>(null);

  const safeActions = Array.isArray(actions) ? actions : [];

  // Filter actions
  const filteredActions = useMemo(() => {
    return safeActions.filter((action) => {
      if (!action) return false;
      if (selectedWorkstream !== 'all' && action.workstream !== selectedWorkstream) return false;
      if (selectedRisk !== 'all' && action.riskLevel !== selectedRisk) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const agent = (action.agentName ?? '').toLowerCase();
        const type = (action.actionType ?? '').toLowerCase();
        const desc = (action.description ?? '').toLowerCase();
        const entity = (action.targetEntity?.name ?? '').toLowerCase();
        return agent.includes(q) || type.includes(q) || desc.includes(q) || entity.includes(q);
      }
      return true;
    });
  }, [safeActions, selectedWorkstream, selectedRisk, searchQuery]);

  const handleApprove = async (actionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setProcessingId(actionId);
    try {
      await onApprove(actionId);
    } catch (err) {
      console.error('Approve error:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleOpenRejectModal = (actionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRejectingActionId(actionId);
    setRejectReason('');
  };

  const handleConfirmReject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rejectingActionId || !rejectReason.trim()) return;
    setProcessingId(rejectingActionId);
    try {
      await onReject(rejectingActionId, rejectReason);
    } catch (err) {
      console.error('Reject error:', err);
    } finally {
      setProcessingId(null);
      setRejectingActionId(null);
      setRejectReason('');
    }
  };

  return (
    <div className="space-y-6">
      {/* Search & Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-sm">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            type="text"
            placeholder="Search pending actions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-1.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:border-mantis-500"
          />
        </div>

        <div className="flex items-center space-x-3 w-full sm:w-auto">
          <div className="flex items-center space-x-1.5 text-xs text-slate-500">
            <Filter className="w-3.5 h-3.5" />
            <select
              value={selectedWorkstream}
              onChange={(e) => setSelectedWorkstream(e.target.value)}
              className="bg-white border border-slate-200 text-xs text-slate-800 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-mantis-500"
            >
              <option value="all">All Workstreams</option>
              <option value="marketing">Marketing</option>
              <option value="referrals">Referrals</option>
              <option value="intake">Intake</option>
              <option value="scheduling">Scheduling</option>
              <option value="rcm">RCM</option>
              <option value="hr">HR & Credentialing</option>
              <option value="finance">Finance</option>
              <option value="clinical-ops">Clinical Ops</option>
            </select>
          </div>

          <div className="flex items-center space-x-1.5 text-xs text-slate-500">
            <select
              value={selectedRisk}
              onChange={(e) => setSelectedRisk(e.target.value)}
              className="bg-white border border-slate-200 text-xs text-slate-800 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-mantis-500"
            >
              <option value="all">All Risk Levels</option>
              <option value="low">Low Risk</option>
              <option value="medium">Medium Risk</option>
              <option value="high">High Risk</option>
              <option value="critical">Critical Risk</option>
            </select>
          </div>
        </div>
      </div>

      {/* Queue Counter */}
      <div className="flex justify-between items-center text-xs text-slate-500 px-1">
        <span>
          Showing <strong className="text-slate-800">{filteredActions.length}</strong>
          {totalPending != null && totalPending > filteredActions.length
            ? <> of <strong className="text-slate-800">{totalPending}</strong> pending</>
            : <> pending</>} review
        </span>
        {totalPending != null && totalPending > actions.length && (
          <span className="text-slate-400">
            Top {actions.length} by criticality — {totalPending - actions.length} more pending
          </span>
        )}
      </div>

      {/* Loading & Empty State */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 h-28 animate-pulse" />
          ))}
        </div>
      ) : filteredActions.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-500 space-y-2">
          <Sparkles className="w-8 h-8 text-mantis-600 mx-auto" />
          <p className="text-xs font-semibold text-slate-800">No pending approvals</p>
          <p className="text-[11px] text-slate-500">All agent actions have been reviewed or match no filters.</p>
        </div>
      ) : (
        /* Cards List */
        <div className="space-y-3">
          {filteredActions.map((action) => {
            const isProcessing = processingId === action.actionId;
            const wsClass = workstreamBadges[action.workstream] ?? 'bg-slate-100 text-slate-700 border-slate-200';
            const riskClass = riskBadges[action.riskLevel] ?? 'bg-slate-100 text-slate-700 border-slate-200';

            return (
              <div
                key={action.actionId}
                onClick={() => onSelectAction(action)}
                className="bg-white border border-slate-200 hover:border-slate-300 rounded-xl p-4 transition-all shadow-sm cursor-pointer space-y-3"
              >
                {/* Header Row */}
                <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${wsClass}`}>
                      {(action.workstream ?? 'general').toUpperCase()}
                    </span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${riskClass}`}>
                      <ShieldAlert className="w-3 h-3 inline mr-1" />
                      {(action.riskLevel ?? 'medium').toUpperCase()} RISK
                    </span>
                  </div>

                  <div className="text-[11px] text-slate-500 flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>{action.proposedAt ? new Date(action.proposedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'Recently'}</span>
                  </div>
                </div>

                {/* Content */}
                <div>
                  <h3 className="text-sm font-bold text-slate-800">{action.actionType ?? 'Pending Action'}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Agent: <span className="font-semibold text-slate-700">{action.agentName ?? 'Agent'}</span>
                    {action.targetEntity?.name && (
                      <span className="ml-2 text-slate-500">Target: <span className="text-slate-700 font-medium">{action.targetEntity.name}</span></span>
                    )}
                  </p>
                  <p className="text-xs text-slate-600 mt-2 line-clamp-2 leading-relaxed">
                    {action.description ?? 'No description provided.'}
                  </p>
                </div>

                {/* Card Footer Actions */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectAction(action);
                    }}
                    className="flex items-center text-xs font-semibold text-slate-500 hover:text-slate-800 transition-colors"
                  >
                    <Eye className="w-3.5 h-3.5 mr-1" />
                    <span>Details</span>
                  </button>

                  <div className="flex items-center space-x-2">
                    {view === 'approved' ? (
                      onDownloadPacket ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); onDownloadPacket(action.actionId); }}
                          className="flex items-center space-x-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-mantis-300 text-mantis-700 hover:bg-mantis-50 transition-colors"
                          title="Printable work order / letter / brief for this approved action"
                        >
                          <Download className="w-3.5 h-3.5" />
                          <span>Packet</span>
                        </button>
                      ) : null
                    ) : (
                      <>
                        <button
                          disabled={isProcessing}
                          onClick={(e) => handleOpenRejectModal(action.actionId, e)}
                          className="flex items-center space-x-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-redwood-500 hover:bg-redwood-400 text-white transition-colors disabled:opacity-50"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          <span>Reject</span>
                        </button>

                        <button
                          disabled={isProcessing}
                          onClick={(e) => handleApprove(action.actionId, e)}
                          className="flex items-center space-x-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-mantis-600 hover:bg-mantis-500 text-white transition-colors disabled:opacity-50"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>Approve</span>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Single Action Rejection Reason Modal */}
      {rejectingActionId && (
        <div className="fixed inset-0 z-50 bg-slate-500/30 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 max-w-md w-full shadow-xl space-y-4">
            <h3 className="text-sm font-bold text-slate-800">Reject Action</h3>
            <p className="text-xs text-slate-500">Please provide a reason for rejecting this agent proposal:</p>
            <form onSubmit={handleConfirmReject} className="space-y-3">
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection (e.g. Needs clinical review, incorrect billing code)..."
                className="w-full bg-white border border-slate-200 rounded-lg p-2.5 text-xs text-slate-800 focus:outline-none focus:border-mantis-500 h-24"
                required
              />
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setRejectingActionId(null)}
                  className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-redwood-500 hover:bg-redwood-400 text-white transition-colors"
                >
                  Confirm Rejection
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
